/**
 * /suprimentos/inventario — sessões de inventário (D31).
 *
 * ABERTA → CONTAGEM → (RECONTAGEM) → AGUARDANDO_APROVACAO → ENCERRADA (gera os ajustes)
 * Contagem cega: quem conta não vê o saldo do sistema; quem aprova vê.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Checkbox from "../../components/Checkbox";
import { FiAlertCircle, FiPlus, FiRefreshCw } from "react-icons/fi";

import AsyncButton from "../../components/AsyncButton";
import EntityHeader from "../../components/EntityHeader";
import KpiStrip from "../../components/KpiStrip";
import SlideOverPanel from "../../components/SlideOverPanel";
import { useAuth } from "../../context/AuthContext";
import { listarDepositos, listarProdutos } from "../../services/estoque";
import {
  abrirInventario, adicionarItemInventario, aprovarInventario, cancelarInventario,
  contarInventario, dispensarRecontagemInventario, enviarInventarioParaAprovacao, ESTADO_INV_LABEL, ESTADO_INV_TONE,
  ESTADOS_INVENTARIO, iniciarContagemInventario, linhasDe, listarBoxes, listarInventarios,
  obterInventario,
} from "../../services/suprimentos";
import { fmtData, fmtQtd } from "./helpers";
import {
  Card, ErrorMsg, Field, Form, FormFooter, InlineHint, Input, Pill,
  PrimaryButton, SecondaryButton, Select, Table, TableWrapper, Textarea, TruncCell,
} from "./styles";
import { Carregando, Vazio } from "./_shared/Estados";
import { situacaoDaRecontagem } from "./inventarioRecontagem";

const NOVO_VAZIO = { deposito: "", box: "", contagem_cega: true, observacao: "" };

/** 2a contagem: campo so onde precisa, e explica quando nao da para recontar. */
function SegundaContagem({ item, situacao, valor, onChange }) {
  if (situacao === "editavel") {
    return (
      <Input type="number" step="0.001" min="0" style={{ maxWidth: 110 }} placeholder="recontar"
        value={valor} onChange={(e) => onChange(e.target.value)} aria-label={`Recontagem de ${item.produto_codigo}`} />
    );
  }
  if (situacao === "dispensada") {
    return (
      <span title={`${item.justificativa_dispensa}${item.recontagem_dispensada_por_nome ? ` — ${item.recontagem_dispensada_por_nome}` : ""}`}>
        <Pill $tone="neutral">dispensada</Pill>
      </span>
    );
  }
  if (situacao === "outra_pessoa") {
    return <span style={{ fontSize: 12, color: "var(--ink-3)" }}>outra pessoa reconta</span>;
  }
  return fmtQtd(item.contagem_2);
}

export default function Inventario() {
  const { request, isMaster, hasAllPermissions, usuario } = useAuth();
  const pode = (p) => isMaster || hasAllPermissions([p]);
  const podeAbrir = pode("inventario:create");
  const podeContar = podeAbrir || pode("inventario:edit");
  const podeAprovar = pode("inventario:aprovar");

  const [sessoes, setSessoes] = useState([]);
  const [depositos, setDepositos] = useState([]);
  const [boxes, setBoxes] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [novo, setNovo] = useState(null);
  const [detalhe, setDetalhe] = useState(null);
  const [contagens, setContagens] = useState({});
  const [produtoExtra, setProdutoExtra] = useState("");
  // Dispensa da recontagem: texto da justificativa (null = painel fechado).
  const [dispensa, setDispensa] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      setSessoes(linhasDe(await listarInventarios(request, { estado: filtro })));
      setErro(null);
    } catch (err) {
      setErro(err?.payload?.detalhe || err?.message || "Erro ao carregar inventários.");
    } finally {
      setLoading(false);
    }
  }, [request, filtro]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    listarDepositos(request).then((p) => setDepositos(linhasDe(p))).catch(() => setDepositos([]));
    listarBoxes(request, { ativo: true }).then((p) => setBoxes(linhasDe(p))).catch(() => setBoxes([]));
    listarProdutos(request, { pageSize: 500, ativo: true }).then((p) => setProdutos(linhasDe(p))).catch(() => setProdutos([]));
  }, [request]);

  const kpis = useMemo(() => {
    const por = (e) => sessoes.filter((s) => s.estado === e).length;
    return [
      { label: "Em contagem", value: por("CONTAGEM") + por("RECONTAGEM"), tone: "info" },
      { label: "Aguardando aprovação", value: por("AGUARDANDO_APROVACAO"), tone: por("AGUARDANDO_APROVACAO") ? "warning" : "neutral" },
      { label: "Encerradas", value: por("ENCERRADA"), tone: "success" },
    ];
  }, [sessoes]);

  const abrirDetalhe = async (id) => {
    const s = await obterInventario(request, id);
    setDetalhe(s);
    setContagens({});
    setDispensa(null);
  };

  const atualizarDetalhe = (s) => {
    setDetalhe(s);
    setContagens({});
    setDispensa(null);
    return carregar();
  };

  const handleAbrir = async (e) => {
    e.preventDefault();
    if (!novo.deposito) { setErro("Escolha o depósito."); return; }
    setErro(null);
    try {
      const s = await abrirInventario(request, {
        deposito: novo.deposito, box: novo.box || null,
        contagem_cega: novo.contagem_cega, observacao: novo.observacao || "",
      });
      setNovo(null);
      await carregar();
      setDetalhe(s);
    } catch (err) {
      setErro(err?.payload?.detalhe || err?.message || "Erro ao abrir inventário.");
    }
  };

  const salvarContagens = async () => {
    const itens = Object.entries(contagens)
      .filter(([, q]) => q !== "" && q !== undefined)
      .map(([item, quantidade]) => ({ item, quantidade }));
    if (!itens.length) throw new Error("Digite ao menos uma contagem.");
    await atualizarDetalhe(await contarInventario(request, detalhe.id, itens));
  };

  const emContagem = detalhe?.estado === "CONTAGEM" || detalhe?.estado === "RECONTAGEM";
  const emRecontagem = detalhe?.estado === "RECONTAGEM";
  const pendentesDeRecontagem = (detalhe?.itens || []).filter((i) => i.precisa_recontagem);
  const souQuemContouTodos = pendentesDeRecontagem.length > 0
    && pendentesDeRecontagem.every((i) => i.contado_por_1 && i.contado_por_1 === usuario?.id);
  const mostraSaldo = detalhe?.itens?.some((i) => i.saldo_sistema !== null && i.saldo_sistema !== undefined);

  return (
    <>
      <EntityHeader
        title="Inventário"
        subtitle="Contagem formal por depósito: o sistema só ajusta o saldo depois da aprovação."
        primaryAction={podeAbrir ? { label: "Abrir inventário", icon: <FiPlus size={14} />, onClick: () => setNovo({ ...NOVO_VAZIO }) } : undefined}
        actions={[{ label: "Atualizar", icon: <FiRefreshCw size={14} />, onClick: carregar }]}
      />
      <KpiStrip items={kpis} />

      <Card>
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          <Select value={filtro} onChange={(e) => setFiltro(e.target.value)}>
            <option value="">Todos os estados</option>
            {ESTADOS_INVENTARIO.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </Select>
        </div>
        {erro && <ErrorMsg><FiAlertCircle size={14} /> {erro}</ErrorMsg>}
        {loading ? <Carregando /> : sessoes.length === 0 ? (
          <Vazio titulo="Nenhuma sessão de inventário" />
        ) : (
          <TableWrapper>
            <Table>
              <colgroup>
                <col style={{ width: "16%" }} /><col style={{ width: "26%" }} /><col style={{ width: "12%" }} />
                <col style={{ width: "16%" }} /><col style={{ width: "14%" }} /><col style={{ width: "16%" }} />
              </colgroup>
              <thead><tr><th>Número</th><th>Depósito / Box</th><th>Itens</th><th>Estado</th><th>Aberta em</th><th>Por</th></tr></thead>
              <tbody>
                {sessoes.map((s) => (
                  <tr key={s.id} style={{ cursor: "pointer" }} onClick={() => abrirDetalhe(s.id)}>
                    <td><TruncCell>{s.numero}</TruncCell></td>
                    <td title={s.deposito_nome}><TruncCell>{s.deposito_nome}{s.box_codigo ? ` · ${s.box_codigo}` : ""}</TruncCell></td>
                    <td>{s.itens_contados}/{s.total_itens}</td>
                    <td><Pill $tone={ESTADO_INV_TONE[s.estado]}>{ESTADO_INV_LABEL[s.estado] || s.estado_display}</Pill></td>
                    <td>{fmtData(s.created_at)}</td>
                    <td><TruncCell>{s.aberta_por_nome || "—"}</TruncCell></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        )}
      </Card>

      <SlideOverPanel open={Boolean(novo)} onClose={() => setNovo(null)} title="Abrir inventário">
        {novo && (
          <Form onSubmit={handleAbrir}>
            <Field>
              Depósito *
              <Select value={novo.deposito} onChange={(e) => setNovo((p) => ({ ...p, deposito: e.target.value }))} required>
                <option value="">— Selecione —</option>
                {depositos.filter((d) => d.ativo).map((d) => <option key={d.id} value={d.id}>{d.nome}</option>)}
              </Select>
            </Field>
            <Field>
              Box (só sobras)
              <Select value={novo.box} onChange={(e) => setNovo((p) => ({ ...p, box: e.target.value }))}>
                <option value="">— Depósito inteiro —</option>
                {boxes.map((b) => <option key={b.id} value={b.id}>{b.codigo}</option>)}
              </Select>
            </Field>
            <Field>
              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <Checkbox checked={novo.contagem_cega} onChange={(e) => setNovo((p) => ({ ...p, contagem_cega: e.target.checked }))} />
                Contagem cega (quem conta não vê o saldo do sistema)
              </label>
            </Field>
            <Field>
              Observação
              <Textarea rows={2} value={novo.observacao} onChange={(e) => setNovo((p) => ({ ...p, observacao: e.target.value }))} />
            </Field>
            <InlineHint>Ao abrir, o saldo de cada produto do depósito é congelado como referência (snapshot).</InlineHint>
            {erro && <ErrorMsg><FiAlertCircle size={14} /> {erro}</ErrorMsg>}
            <FormFooter>
              <SecondaryButton type="button" onClick={() => setNovo(null)}>Cancelar</SecondaryButton>
              <PrimaryButton type="submit">Abrir</PrimaryButton>
            </FormFooter>
          </Form>
        )}
      </SlideOverPanel>

      <SlideOverPanel
        open={Boolean(detalhe)}
        onClose={() => setDetalhe(null)}
        title={detalhe?.numero || "Inventário"}
        subtitle={detalhe ? `${detalhe.deposito_nome}${detalhe.box_codigo ? ` · ${detalhe.box_codigo}` : ""}` : ""}
        width="min(96vw, 820px)"
        footer={detalhe && (
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
            {podeAbrir && detalhe.estado === "ABERTA" && (
              <AsyncButton successMessage="Contagem iniciada." onClick={async () => atualizarDetalhe(await iniciarContagemInventario(request, detalhe.id))}>Iniciar contagem</AsyncButton>
            )}
            {podeContar && emContagem && (
              <AsyncButton variant="secondary" successMessage="Contagens gravadas." onClick={salvarContagens}>Gravar contagens</AsyncButton>
            )}
            {podeAbrir && emContagem && (
              <AsyncButton successMessage="Enviado para aprovação." onClick={async () => atualizarDetalhe(await enviarInventarioParaAprovacao(request, detalhe.id))}>Enviar para aprovação</AsyncButton>
            )}
            {podeAprovar && emRecontagem && pendentesDeRecontagem.length > 0 && dispensa === null && (
              <SecondaryButton type="button" onClick={() => setDispensa("")}>
                Dispensar recontagem ({pendentesDeRecontagem.length})
              </SecondaryButton>
            )}
            {podeAprovar && detalhe.estado === "AGUARDANDO_APROVACAO" && (
              <AsyncButton successMessage="Inventário aprovado: ajustes gerados." onClick={async () => atualizarDetalhe(await aprovarInventario(request, detalhe.id))}>Aprovar e ajustar saldo</AsyncButton>
            )}
            {podeAbrir && !["ENCERRADA", "CANCELADA"].includes(detalhe.estado) && (
              <AsyncButton variant="danger" successMessage="Inventário cancelado." onClick={async () => {
                const motivo = window.prompt("Motivo do cancelamento:");
                if (motivo === null) return;
                await atualizarDetalhe(await cancelarInventario(request, detalhe.id, motivo));
              }}>Cancelar</AsyncButton>
            )}
          </div>
        )}
      >
        {detalhe && (
          <>
            <div style={{ marginBottom: 12, display: "flex", gap: 8, alignItems: "center" }}>
              <Pill $tone={ESTADO_INV_TONE[detalhe.estado]}>{ESTADO_INV_LABEL[detalhe.estado]}</Pill>
              {detalhe.contagem_cega ? <Pill $tone="neutral">contagem cega</Pill> : null}
              <span style={{ fontSize: 12.5, color: "var(--ink-3)" }}>Snapshot: {detalhe.snapshot_em ? new Date(detalhe.snapshot_em).toLocaleString("pt-BR") : "—"}</span>
            </div>
            {emRecontagem && pendentesDeRecontagem.length > 0 ? (
              <InlineHint style={{ marginBottom: 10 }}>
                {pendentesDeRecontagem.length} {pendentesDeRecontagem.length === 1 ? "item divergiu e precisa" : "itens divergiram e precisam"} de
                uma 2ª contagem feita por outra pessoa.
                {souQuemContouTodos ? " Você fez a 1ª contagem deles." : ""}
                {podeAprovar ? " Como você aprova inventário, pode dispensar a recontagem com uma justificativa." : " Quem aprova inventário pode dispensar a recontagem."}
              </InlineHint>
            ) : null}
            {dispensa !== null ? (
              <div style={{ display: "grid", gap: 8, marginBottom: 12, padding: 12, border: "1px solid var(--border)", borderRadius: 10 }}>
                <Field>
                  Por que dispensar a recontagem de {pendentesDeRecontagem.length} {pendentesDeRecontagem.length === 1 ? "item" : "itens"}? *
                  <Textarea
                    rows={2} value={dispensa} onChange={(e) => setDispensa(e.target.value)} maxLength={300}
                    placeholder="Ex.: acerto inicial — o saldo do sistema estava sem as entradas das chapas."
                    aria-label="Justificativa da dispensa"
                  />
                </Field>
                <InlineHint>Fica registrado no item e no histórico: quem dispensou, quando e por quê. Depois, envie para aprovação.</InlineHint>
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <SecondaryButton type="button" onClick={() => setDispensa(null)}>Voltar</SecondaryButton>
                  <AsyncButton
                    successMessage="Recontagem dispensada."
                    onClick={async () => atualizarDetalhe(await dispensarRecontagemInventario(request, detalhe.id, dispensa.trim()))}
                  >
                    Dispensar
                  </AsyncButton>
                </div>
              </div>
            ) : null}
            {podeContar && emContagem && (
              <div style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "center" }}>
                <Select value={produtoExtra} onChange={(e) => setProdutoExtra(e.target.value)} style={{ maxWidth: 320 }}>
                  <option value="">Achou algo fora da lista? Adicione o produto…</option>
                  {produtos.filter((p) => !detalhe.itens.some((i) => i.produto === p.id)).map((p) => (
                    <option key={p.id} value={p.id}>{p.codigo} — {p.nome}</option>
                  ))}
                </Select>
                <AsyncButton variant="secondary" silentSuccess onClick={async () => {
                  if (!produtoExtra) return;
                  await atualizarDetalhe(await adicionarItemInventario(request, detalhe.id, produtoExtra));
                  setProdutoExtra("");
                }}>Adicionar</AsyncButton>
              </div>
            )}
            <TableWrapper>
              <Table>
                <colgroup>
                  <col style={{ width: "34%" }} />
                  {mostraSaldo ? <col style={{ width: "14%" }} /> : null}
                  <col style={{ width: "16%" }} /><col style={{ width: "16%" }} />
                  {mostraSaldo ? <col style={{ width: "12%" }} /> : null}
                </colgroup>
                <thead>
                  <tr>
                    <th>Produto</th>
                    {mostraSaldo ? <th>Sistema</th> : null}
                    <th>1ª contagem</th>
                    <th>2ª contagem</th>
                    {mostraSaldo ? <th>Diferença</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {(detalhe.itens || []).map((it) => {
                    const div = it.divergencia === null || it.divergencia === undefined ? null : Number(it.divergencia);
                    return (
                      <tr key={it.id}>
                        <td title={it.produto_nome}><TruncCell>{it.produto_codigo} — {it.produto_nome} ({it.produto_unidade})</TruncCell></td>
                        {mostraSaldo ? <td>{fmtQtd(it.saldo_sistema)}</td> : null}
                        <td>
                          {/* Na 1a contagem da para corrigir o que ja foi gravado (o backend
                              regrava a 1a enquanto a sessao esta em CONTAGEM). */}
                          {podeContar && detalhe.estado === "CONTAGEM" ? (
                            <Input type="number" step="0.001" min="0" style={{ maxWidth: 110 }}
                              placeholder={it.contagem_1 !== null ? fmtQtd(it.contagem_1) : ""}
                              aria-label={`Contagem de ${it.produto_codigo}`}
                              value={contagens[it.id] ?? ""} onChange={(e) => setContagens((p) => ({ ...p, [it.id]: e.target.value }))} />
                          ) : fmtQtd(it.contagem_1)}
                        </td>
                        <td>
                          <SegundaContagem
                            item={it}
                            situacao={situacaoDaRecontagem(it, { emRecontagem, podeContar, usuarioId: usuario?.id })}
                            valor={contagens[it.id] ?? ""}
                            onChange={(v) => setContagens((p) => ({ ...p, [it.id]: v }))}
                          />
                        </td>
                        {mostraSaldo ? (
                          <td>
                            {div === null ? "—" : (
                              <Pill $tone={div === 0 ? "success" : "warning"}>{div > 0 ? "+" : ""}{fmtQtd(div)}</Pill>
                            )}
                          </td>
                        ) : null}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </TableWrapper>
          </>
        )}
      </SlideOverPanel>
    </>
  );
}
